import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Download, X, HandCoins } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export default function HandoversPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emptyForm = { amount: "", handed_to: "", note: "", date: todayISO() };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await api.get("/cash-handovers", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (h) => {
    setEditingId(h.id);
    setForm({
      amount: String(h.amount),
      handed_to: h.handed_to || "",
      note: h.note || "",
      date: (h.date || "").slice(0, 10) || todayISO(),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    if (!form.handed_to.trim()) return toast.error("Recipient name is required");
    setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editingId) {
        await api.patch(`/cash-handovers/${editingId}`, payload);
        toast.success("Handover updated");
      } else {
        await api.post("/cash-handovers", payload);
        toast.success("Handover recorded");
      }
      setEditingId(null);
      setForm(emptyForm);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save handover");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this handover? Cash at Cafe will increase by this amount.")) return;
    try {
      await api.delete(`/cash-handovers/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/cash-handovers/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `handovers_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const clearDates = () => { setStartDate(""); setEndDate(""); };
  const canModify = (h) => user?.role === "admin" || h.created_by === user?.id;
  const total = rows.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto animate-fade-in-up" data-testid="handovers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">Cash Trail</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-espresso-900">
            Handovers
          </h1>
          <p className="text-taupe-600 mt-2 text-sm">
            {rows.length} entries · <span className="font-semibold text-espresso-800">{formatINR(total)}</span> total
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={exportCsv}
            disabled={exporting || rows.length === 0}
            variant="outline"
            className="rounded-xl border-taupe-200 hover:border-espresso-800 hover:bg-taupe-100 text-espresso-800 font-semibold h-11"
            data-testid="export-handovers-button"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>

          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button
                type="button"
                onClick={openAdd}
                className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold px-6 h-11"
                data-testid="add-handover-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Record handover
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight text-espresso-900 flex items-center gap-2">
                  <HandCoins className="w-5 h-5" /> {editingId ? "Edit handover" : "Record handover"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="handover-form-page">
                <div>
                  <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Amount (₹)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                    data-testid="handover-page-amount"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Handed to</Label>
                  <Input
                    required
                    value={form.handed_to}
                    onChange={(e) => setForm({ ...form, handed_to: e.target.value })}
                    placeholder="e.g. Vinay"
                    className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                    data-testid="handover-page-recipient"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Date</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                    data-testid="handover-page-date"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Note (optional)</Label>
                  <Textarea
                    rows={2}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="e.g. Evening deposit"
                    className="rounded-xl bg-taupe-100 border-taupe-200"
                    data-testid="handover-page-note"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold"
                    data-testid="submit-handover-page-button"
                  >
                    {submitting ? "Saving…" : editingId ? "Update handover" : "Save handover"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Date range filter */}
      <div className="card-soft p-4 sm:p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="handovers-filters">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="handovers-start-date"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">To</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="handovers-end-date"
          />
        </div>
        {(startDate || endDate) && (
          <Button
            type="button"
            variant="ghost"
            onClick={clearDates}
            className="rounded-xl h-11 text-taupe-600 hover:text-espresso-800"
            data-testid="clear-handovers-filter"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="card-soft overflow-hidden" data-testid="handovers-table-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-taupe-100 text-taupe-600 text-xs font-semibold uppercase tracking-wider">
                <th className="text-left py-4 px-6">Date</th>
                <th className="text-left py-4 px-6">Handed to</th>
                <th className="text-left py-4 px-6">Note</th>
                <th className="text-right py-4 px-6">Amount</th>
                <th className="text-right py-4 px-6" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="py-8 text-center text-taupe-500 text-sm">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="py-12 text-center text-taupe-500 text-sm" data-testid="handovers-empty">
                  No handovers yet.
                </td></tr>
              )}
              {rows.map((h) => (
                <tr key={h.id} className="border-t border-taupe-200 hover:bg-taupe-100/40 transition-colors" data-testid="handovers-row">
                  <td className="py-4 px-6 text-sm text-espresso-900">{new Date(h.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="py-4 px-6 text-sm font-semibold text-espresso-900">{h.handed_to}</td>
                  <td className="py-4 px-6 text-sm text-taupe-700 max-w-[320px] truncate">{h.note || <span className="text-taupe-500">—</span>}</td>
                  <td className="py-4 px-6 text-right font-semibold text-espresso-900">{formatINR(h.amount)}</td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openEdit(h)}
                        disabled={!canModify(h)}
                        className="p-2 rounded-lg text-espresso-800 hover:bg-taupe-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`edit-handover-${h.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(h.id)}
                        disabled={!canModify(h)}
                        className="p-2 rounded-lg text-terracotta-500 hover:bg-terracotta-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`delete-handover-row-${h.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
